import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useMunicipalStore } from "../../store/municipalStore";
import MetricCard from "../shared/MetricCard";
import { Header, Container, PageGrid } from "../shared/Layout";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, Users, Clock, CheckCircle2 } from "lucide-react";

export default function AlcaldeDashboard() {
  const requests = useMunicipalStore(state => state.requests);
  const areas = useMunicipalStore(state => state.areas);

  const stats = useMemo(() => {
    const byState = {};
    const byArea = {};
    const byUrgency = {};
    let totalTime = 0;
    let resolvedCount = 0;

    requests.forEach(r => {
      byState[r.estado] = (byState[r.estado] || 0) + 1;
      byArea[r.area] = (byArea[r.area] || 0) + 1;
      byUrgency[r.urgencia] = (byUrgency[r.urgencia] || 0) + 1;
      
      if (r.estado === "resuelto") {
        resolvedCount++;
        totalTime += Date.now() - r.createdAt;
      }
    });

    return {
      total: requests.length,
      byState,
      byArea,
      byUrgency,
      avgResolutionTime: resolvedCount > 0 ? Math.round(totalTime / resolvedCount / 1000 / 60 / 60) : 0,
      resueltas: byState["resuelto"] || 0,
      enProceso: byState["en_proceso"] || 0
    };
  }, [requests]);

  const chartDataByArea = useMemo(() => {
    return Object.entries(stats.byArea).map(([area, count]) => ({
      name: area,
      value: count
    }));
  }, [stats]);

  const chartDataByState = useMemo(() => {
    const stateMap = {
      "enviado": "Recibidas",
      "clasificado": "Clasificadas",
      "asignado": "Asignadas",
      "en_proceso": "En Proceso",
      "resuelto": "Resueltas"
    };

    return Object.entries(stats.byState).map(([state, count]) => ({
      name: stateMap[state] || state,
      value: count
    }));
  }, [stats]);

  const COLORS = ["#0066cc", "#6f42c1", "#ffd700", "#ff9800", "#28a745"];

  return (
    <Container>
      <Header
        title="Centro de Control"
        subtitle="Dashboard ejecutivo de solicitudes municipales"
        icon={TrendingUp}
      />

      {/* Métricas Principales */}
      <PageGrid cols={4}>
        <MetricCard
          title="Total de Solicitudes"
          value={stats.total}
          icon={Users}
          color="blue"
          trend={stats.total > 0 ? { positive: true, value: 12, label: "este mes" } : null}
        />
        <MetricCard
          title="En Proceso"
          value={stats.enProceso}
          icon={Clock}
          color="orange"
        />
        <MetricCard
          title="Resueltas"
          value={stats.resueltas}
          icon={CheckCircle2}
          color="green"
          trend={stats.resueltas > 0 ? { positive: true, value: 8, label: "esta semana" } : null}
        />
        <MetricCard
          title="Promedio de Resolución"
          value={`${stats.avgResolutionTime}h`}
          icon={Clock}
          color="purple"
          subtitle="Tiempo promedio"
        />
      </PageGrid>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Solicitudes por Área */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-6">Solicitudes por Área</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartDataByArea}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartDataByArea.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Solicitudes por Estado */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-6">Estado de Solicitudes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartDataByState}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#0066cc" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Distribución por Urgencia */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
      >
        <h3 className="text-lg font-bold text-gray-900 mb-6">Distribución por Urgencia</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(stats.byUrgency).map(([urgency, count]) => (
            <div key={urgency} className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl">
              <p className="text-3xl font-bold text-blue-600 mb-2">
                {urgency === "baja" ? "🟢" : urgency === "media" ? "🟡" : "🔴"}
              </p>
              <p className="font-semibold text-gray-900 capitalize">{urgency}</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{count}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </Container>
  );
}
